import React, { memo, useEffect, useState } from "react";
import { Box, Typography, Checkbox, Collapse, } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchDashboardData } from "../../../store/dashboard/action";

const DISCOUNT_OPTIONS = [
  { label: "All Offers", value: "" },
  { label: "0% - 5%", value: "0-5" },
  { label: "5% - 10%", value: "5-10" },
  { label: "10%+", value: "10-plus" },
];

const ProductFilterDesktop = memo(({ categoryIds, brandIds, setSearchParams }) => {
  const dispatch = useDispatch();

  const categories = useSelector((state) => state?.Dashboard?.categories);
  const brands = useSelector((state) => state?.Dashboard?.Brands);
  const [categoriesOpen,] = useState(true);
  const [discountOpen, setDiscountOpen] = useState(true);
  const [priceOpen, setPriceOpen] = useState(true);
  const [brandOpen, setBrandOpen] = useState(true);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [brandSearch, setBrandSearch] = useState("");

  useEffect(() => {
    if (!categories?.length) {
      dispatch(fetchDashboardData());
    }
  }, [categories, dispatch]);

  const handleToggle = (id) => {
    const selectedId = String(id);

    let updatedCategories = [];
    if (categoryIds?.includes(selectedId)) {
      updatedCategories = categoryIds?.filter((item) => item !== selectedId);
    } else {
      updatedCategories = [...categoryIds, selectedId];
    }

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (updatedCategories?.length > 0) {
        params.set("categoryIds[]", updatedCategories?.join(","));
      } else {
        params.delete("categoryIds[]");
      }
      return params;
    });
  };

  const handleAllCategoriesToggle = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("categoryIds[]");
      return params;
    });
  };

  const handleDiscountChange = (value) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) {
        params.set("discountRange", value);
      } else {
        params.delete("discountRange");
      }
      return params;
    });
  };

  const currentDiscount = new URLSearchParams(window.location.search).get("discountRange") || "";

  const handlePriceApply = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (minPrice) {
        params.set("minPlatformAmount", minPrice);
      } else {
        params.delete("minPlatformAmount");
      }
      if (maxPrice) {
        params.set("maxPlatformAmount", maxPrice);
      } else {
        params.delete("maxPlatformAmount");
      }
      return params;
    });
  };

  const handleBrandToggle = (id) => {
    const selectedId = String(id);

    const updatedBrands = brandIds.includes(selectedId)
      ? brandIds.filter((item) => item !== selectedId)
      : [...brandIds, selectedId];

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);

      if (updatedBrands.length) {
        params.set("brandIds[]", updatedBrands.join(","));
      } else {
        params.delete("brandIds[]");
      }

      return params;
    });
  };

  const filteredBrands = (brands || []).filter((brand) =>
    brand?.name?.toLowerCase().includes(brandSearch.toLowerCase())
  );


  const handleReset = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("categoryIds[]");
      params.delete("discountRange");
      params.delete("minPlatformAmount");
      params.delete("maxPlatformAmount");
      params.delete("brandIds[]");
      return params;
    });
    setMinPrice("");
    setMaxPrice("");
    setBrandSearch("");
  };
  const categoryOrder = [
    "Apparel",
    "Footwear",
    "E-Commerce",
    "Jewellery",
    "Entertainment",
    "Accessories",
    "Gaming",
    "Electronic",
    "Furniture",
    "Health",
    "Grocery",
    "Mall",
    "Cinema"
  ];
  const CatName = categoryOrder
    .map((categoryName) =>
      categories?.find(
        (item) =>
          item?.name?.trim().toLowerCase() ===
          categoryName.toLowerCase()
      )
    )
    .filter(Boolean);


  const SectionHeader = ({ title, isOpen, onToggle }) => (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer",
        px: 2,
        py: 1.25,
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontFamily: "Be Vietnam Pro",
          fontSize: "15px",
          color: "primary.main",
        }}
      >
        {title}
      </Typography>
      {isOpen ? (
        <ExpandLessIcon sx={{ fontSize: 20, color: "#999" }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 20, color: "#999" }} />
      )}
    </Box>
  );

  const FilterRow = ({ label, count, checked, onClick }) => (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.75,
        cursor: "pointer",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Be Vietnam Pro",
          fontSize: "13.5px",
          color: "#333333",
          fontWeight: 500,
          textTransform: "capitalize",
        }}
      >
        {label}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {typeof count === "number" && (
          <Typography sx={{ fontSize: "12px", color: "#999" }}>{count}</Typography>
        )}
        <Checkbox
          size="small"
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={onClick}
          sx={{
            p: 0.5,
            color: "#D9D9D9",
            "&.Mui-checked": { color: "primary.main" },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid #E5E5E5",
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        overflow: "hidden",
        mt: 4.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1.75,
          borderBottom: "1px solid #EEEEEE",
        }}
      >
        <Typography
          sx={{
            color: "#1A1A1A",
            fontFamily: "Be Vietnam Pro",
            fontWeight: 700,
            fontSize: "16px",
          }}
        >
          Filters
        </Typography>

        <Typography
          onClick={handleReset}
          sx={{
            color: "primary.main",
            fontFamily: "Be Vietnam Pro",
            fontWeight: 600,
            fontSize: "13px",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Clear All
        </Typography>
      </Box>

      <Box sx={{ borderBottom: "1px solid #EEEEEE" }}>
        <SectionHeader
          title="Categories"
          isOpen={categoriesOpen}

        />
        <Collapse in={categoriesOpen}>
          <Box sx={{ px: 2, pb: 1.5 }}>
            <FilterRow
              label="All Categories"

              checked={categoryIds?.length === 0}
              onClick={handleAllCategoriesToggle}
            />

            {CatName?.map((category) => {
              const isSelected = categoryIds.includes(String(category?.id));
              return (
                <FilterRow
                  key={category?.id}
                  label={category?.name}
                  count={category?.productCount}
                  checked={isSelected}
                  onClick={() => handleToggle(category?.id)}
                />
              );
            })}
          </Box>
        </Collapse>
      </Box>

      {/* <Box sx={{ borderBottom: "1px solid #EEEEEE" }}>
        <SectionHeader
          title="Discount"
          isOpen={discountOpen}
          onToggle={() => setDiscountOpen((prev) => !prev)}
        />
        <Collapse in={discountOpen}>
          <Box sx={{ px: 2, pb: 1.5 }}>
            {DISCOUNT_OPTIONS?.map((option) => (
              <FilterRow
                key={option?.label}
                label={option?.label}
                checked={currentDiscount === option?.value}
                onClick={() => handleDiscountChange(option?.value)}
              />
            ))}
          </Box>
        </Collapse>
      </Box> */}

      {/* <Box sx={{ borderBottom: "1px solid #EEEEEE" }}>
        <SectionHeader
          title="Price Range (AED)"
          isOpen={priceOpen}
          onToggle={() => setPriceOpen((prev) => !prev)}
        />
        <Collapse in={priceOpen}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <TextField
                type="number"
                placeholder="Min"
                size="small"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "12px" },
                }}
              />
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#000000",
                  fontFamily: "Be Vietnam Pro",
                  fontWeight: 600,
                }}
              >
                To
              </Typography>
              <TextField
                type="number"
                placeholder="Max"
                size="small"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "12px" },
                }}
              />
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={handlePriceApply}
              sx={{
                backgroundColor: "primary.main",
                color: "#fff",
                textTransform: "none",
                fontFamily: "Be Vietnam Pro",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              Apply
            </Button>
          </Box>
        </Collapse>
      </Box> */}

      {/* <Box>
        <SectionHeader
          title="Brand"
          isOpen={brandOpen}
          onToggle={() => setBrandOpen((prev) => !prev)}
        />
        <Collapse in={brandOpen}>
          <Box sx={{ px: 2, pb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search brand..."
              value={brandSearch}
              onChange={(e) => setBrandSearch(e?.target?.value)}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "13px" },
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                maxHeight: "220px",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
              }}
            >
              {filteredBrands.length > 0 ? (
                filteredBrands.map((brand) => (
                  <FilterRow
                    key={brand?.id}
                    label={brand.name}
                    checked={brandIds?.includes(brand.id)}
                    onClick={() => handleBrandToggle(brand.id)}
                  />
                ))
              ) : (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#999",
                    py: 1,
                  }}
                >
                  No brands found.
                </Typography>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box> */}
    </Box>
  );
});

export default ProductFilterDesktop;